<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { useNavigate, useParams } from 'svelte-navigator';
  import { Button, ButtonGroup, Label, Nav, NavItem, NavLink, Spinner } from '@sveltestrap/sveltestrap';

  import { CardEditor, CardPreview, CardPreviewMobile } from '@client/components/bizcard-editor';
  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';
  import { PageHeader } from '@client/components/page';
  import { UserSelect } from '@client/components/user-select';

  import {
    fields,
    id,
    isValid,
    loadBizcard,
    loading,
    loadTemplate,
    renderedTemplate,
    saveBizcard,
    saving,
    selectedUser,
    setSelectedUser,
    template,
    updateBizcard,
    updatedRulename,
    updateVariable,
    variables
  } from '@client/stores/bizcard';
  import { userStore } from '@client/stores/user';

  import Ai from './ai.svelte';
  import Fields from './fields.svelte';
  import Rulename from './rulename.svelte';
  import SwitchTemplates from './switch-templates.svelte';
  import Variables from './variables.svelte';

  const { addNotification } = getNotificationsContext();
  const navigate = useNavigate();
  const params = useParams();

  export let edit = false;

  let originalSource = '';
  let updatingPreview = false;
  let userSelectValue = null;
  let invalidRulename = false;
  let currentTab = 'mobile';
  let users = [];
  let activeMode = 'designer';
  let activeSidebar = 'styles'; // TODO react if no variables or fields
  let templateSwitched = false;
  let originalRulename = '';
  let showAi = undefined;

  onMount(async () => {
    if ($params.id) {
      await loadBizcard($params.id);
      originalSource = $template;
      originalRulename = $id;
      $updatedRulename = $id;
    } else {
      await loadTemplate($params.template);
      originalSource = $template;
      $updatedRulename = $id;
    }
  });

  const onSave = async () => {
    let success;

    if (edit) {
      success = await updateBizcard();
    } else {
      success = await saveBizcard();
    }

    if (success) {
      addNotification({
        text: $format('label.SUCCESS.BUSINESS_CARD_UPDATE'),
        type: 'success',
        autohide: 2500,
        position: 'top-right'
      });

      navigate('/bizcards/studio');
    } else {
      addNotification({
        text: $format('label.FAILURE.BUSINESS_CARD_UPDATE'),
        type: 'error',
        dismissable: true,
        autohide: 2500,
        position: 'top-right'
      });
    }
  };

  $: disableUserSelect = currentTab === 'html';
  $: isDisabled = !$isValid || $saving || invalidRulename;
  $: isLegacy = Object.keys($variables).some((name) => name.startsWith('--bs-'));
  $: showDesign = !isLegacy && Object.keys($variables).length;
  $: showFields = !isLegacy && Object.keys($fields).length;

  $: {
    $template;
    $selectedUser;
    updatingPreview = true;
  }

  $: showLoader = $loading || $saving || !users.length;

  $: if (users.length) {
    const userInDomain = users.some((user) => user.value === $selectedUser?.value);

    if (!userInDomain) {
      setSelectedUser(null);
      userSelectValue = null;
    }

    if (!userSelectValue) {
      if (!$selectedUser) {
        const userToSelect = $userStore.isManaging ? users[0] : $userStore.currentUser;
        setSelectedUser(userToSelect);
      }

      userSelectValue = $selectedUser;
    }
  }

  $: if (templateSwitched) {
    originalSource = $template;

    templateSwitched = false;
  }

  // Detect template changes that add/remove legacy code:
  $: if (isLegacy) {
    activeMode = 'editor';
  }

  const handleCardMessage = (e) => {
    if (e.data?.source === 'card') {
      const { target } = e.data;
      if (target) {
        showAi = target;
      }
    }
  };
</script>

<div id="bizcard-editor" class="page bizcard-editor">
  <Loader show={showLoader} backdrop={showLoader} />

  <PageHeader
    title={$id}
    paths={[
      [$format('label.BUSINESS_CARDS'), '/bizcards'],
      [$format('label.DESIGNER_STUDIO'), '/bizcards/studio'],
      [$format('label.EDIT_BUSINESS_CARD')]
    ]}
    class="shadow-none"
  >
    <div slot="title">
      <Rulename
        bind:name={$updatedRulename}
        bind:invalid={invalidRulename}
        initialRulename={originalRulename}
        isEditor={edit}
      />
    </div>

    {#if showDesign || showFields}
      <ButtonGroup class="me-2">
        <Button
          id="design-btn"
          color="secondary"
          active={activeMode === 'designer'}
          on:click={() => (activeMode = 'designer')}
        >
          <Icon name="droplet" class="me-1" />
          <span class="d-none d-lg-inline">{$format('label.DESIGN')}</span>
        </Button>
        <Button
          id="editor-btn"
          color="secondary"
          active={activeMode === 'editor'}
          on:click={() => (activeMode = 'editor')}
        >
          <Icon name="code" class="me-1" />
          <span class="d-none d-lg-inline">{$format('label.EDIT_HTML')}</span>
        </Button>
      </ButtonGroup>
    {/if}
    <Button color="secondary" class="ms-auto me-2" disabled={$saving} on:click={() => navigate('/bizcards/studio')}>
      {$format('label.CANCEL')}
    </Button>
    <Button color="primary" disabled={isDisabled} on:click={onSave}>
      {$format(edit ? 'label.UPDATE' : 'label.SAVE')}
    </Button>
  </PageHeader>

  <div class="page-content">
    {#if !$loading}
      <div class="layout sidebar" class:edit-html={activeMode === 'editor'}>
        <div class="tabs-left">
          <div class="tabs-left-header">
            <div class="tabs-left-header-user-select">
              <Label class="m-0 pe-2 d-none d-lg-inline" for="selecteduser">
                {$format('label.PREVIEW')}
              </Label>
              <div id="preview-user">
                <UserSelect
                  bind:users
                  value={userSelectValue}
                  on:change={(e) => setSelectedUser(e.detail)}
                  isClearable={false}
                  showChevron
                  disabled={disableUserSelect}
                />
              </div>
            </div>

            <Nav pills>
              <NavItem>
                <NavLink active={currentTab === 'mobile'} on:click={() => (currentTab = 'mobile')}>
                  {#if updatingPreview}
                    <Spinner size="sm" color="secondary" class="border-1" />
                  {:else}
                    <Icon name="smartphone" />
                  {/if}
                  <div class="d-none d-xl-inline-block">{$format('label.MOBILE')}</div>
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink active={currentTab === 'desktop'} on:click={() => (currentTab = 'desktop')}>
                  {#if updatingPreview}
                    <Spinner size="sm" color="secondary" class="border-1" />
                  {:else}
                    <Icon name="monitor" />
                  {/if}
                  <div class="d-none d-xl-inline-block">{$format('label.DESKTOP')}</div>
                </NavLink>
              </NavItem>
            </Nav>
          </div>

          <div class="preview-content">
            {#if showAi}
              <Ai
                target={showAi}
                template={$template}
                on:edit={(e) => {
                  $template = e.detail;
                }}
                on:close={() => {
                  showAi = undefined;
                }}
              />
            {/if}
            {#if currentTab === 'mobile'}
              <div class="mobile-content">
                <CardPreviewMobile>
                  <CardPreview source={$renderedTemplate} on:load={() => (updatingPreview = false)} />
                </CardPreviewMobile>
              </div>
            {:else if currentTab === 'desktop'}
              <iframe
                tabindex="-1"
                style="width: 100%; height: 100%;"
                title={$selectedUser?.name}
                srcdoc={$renderedTemplate}
                on:load={() => (updatingPreview = false)}
              />
            {/if}
          </div>
        </div>

        <aside id="design">
          <Nav pills class="sticky-top bg-white">
            {#if showDesign}
              <NavItem>
                <NavLink active={activeSidebar === 'styles'} on:click={() => (activeSidebar = 'styles')}>
                  <Icon name="droplet" />
                  {$format('label.STYLES')}
                </NavLink>
              </NavItem>
            {/if}
            {#if showFields}
              <NavItem>
                <NavLink active={activeSidebar === 'fields'} on:click={() => (activeSidebar = 'fields')}>
                  <Icon name="server" />
                  {$format('label.FIELDS')}
                </NavLink>
              </NavItem>
            {/if}

            {#if !edit}
              <NavItem>
                <NavLink active={activeSidebar === 'templates'} on:click={() => (activeSidebar = 'templates')}>
                  <Icon name="id-card" />
                  {$format('label.TEMPLATES')}
                </NavLink>
              </NavItem>
            {/if}
          </Nav>

          <div>
            <div class:d-none={!showDesign || activeSidebar !== 'styles'}>
              <Variables variables={$variables} onChange={updateVariable} />
            </div>
            <div class:d-none={!showFields || activeSidebar !== 'fields'}>
              <Fields />
            </div>
            <div class:d-none={activeSidebar !== 'templates'}>
              <SwitchTemplates bind:templateSwitched bind:source={$template} {originalSource} />
            </div>
          </div>
        </aside>

        <aside id="editor">
          <CardEditor bind:source={$template} {originalSource} />
        </aside>
      </div>
    {/if}
  </div>
</div>

<svelte:window on:message={handleCardMessage} />

<style lang="scss" src="./designer.scss"></style>
